import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { MapPin, ArrowRight } from "lucide-react";
import { StoriesMap } from "../components/StoriesMap";
import { Lightbox } from "../components/Lightbox";
import { StoryActions } from "../components/StoryActions";
import { Seo } from "../components/Seo";
import { ShareButton } from "../components/ShareButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const StoryCard = ({ story, onOpen }) => {
  const photos = story.photos || [];
  const place = story.locality || story.location_text;
  return (
    <div className="border-2 border-[#E8F5E9] bg-white flex flex-col" data-testid={`story-card-${story.id}`}>
      {photos.length > 0 && (
        <div className={`grid gap-1 ${photos.length === 1 ? "grid-cols-1" : "grid-cols-2"}`}>
          {photos.slice(0, 4).map((p, i) => (
            <button
              key={p}
              onClick={() => onOpen(photos, i)}
              data-testid={`story-photo-${story.id}-${i}`}
              className={`relative overflow-hidden bg-[#E8F5E9] ${photos.length === 1 ? "aspect-[4/3]" : "aspect-square"}`}
            >
              <img
                src={`${BACKEND}${p}`}
                alt=""
                loading="lazy"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}
              />
              {i === 3 && photos.length > 4 && (
                <span className="absolute inset-0 bg-black/50 text-white font-[700] flex items-center justify-center text-lg">
                  +{photos.length - 4}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
      <div className="p-5 flex flex-col gap-3 flex-1">
        <h3 className="text-lg font-[600] text-[#0D3B4C]">{story.name}</h3>
        {place && (
          <p className="text-sm font-[600] text-[#1B6B2E] flex items-center gap-1">
            <MapPin size={14} /> {place}
          </p>
        )}
        <p className="text-sm font-[400] text-[#0D3B4C] line-clamp-3">{story.story}</p>
        <Link
          to={`/verhalen/${story.id}`}
          data-testid={`read-story-${story.id}`}
          className="inline-flex items-center gap-1 font-[700] text-[#B71C1C] hover:gap-2 transition-all self-start"
        >
          Lees verhaal <ArrowRight size={16} />
        </Link>
        <div className="mt-auto pt-2">
          <StoryActions id={story.id} likes={story.likes} />
        </div>
      </div>
    </div>
  );
};

export default function Stories() {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lb, setLb] = useState(null);

  useEffect(() => {
    axios.get(`${API}/tori/approved`)
      .then((r) => setStories(r.data))
      .catch((e) => console.error("Verhalen laden mislukt:", e))
      .finally(() => setLoading(false));
  }, []);

  const openLb = (photos, index) => setLb({ photos, index });
  const nav = (d) => setLb((prev) => prev && ({ ...prev, index: (prev.index + d + prev.photos.length) % prev.photos.length }));

  return (
    <div className="max-w-[1200px] mx-auto w-full px-6 py-12">
      <Seo
        title="Verhalen | De Zeeuwse Tori"
        description="Lees de verhalen van onze community en ontdek de Zeeuwse Tori kaart met plekken door heel Zeeland."
        image="og-verhalen.jpg"
        path="/verhalen"
      />
      <div className="text-center mb-8">
        <h1 className="text-4xl sm:text-5xl font-[700] text-[#0D3B4C] tracking-tight">Tori's van Zeeland</h1>
        <p className="mt-3 text-lg md:text-xl font-[600] text-[#0D3B4C]">
          Verhalen van onze gasten, op de kaart
        </p>
        <Link
          to="/deel-jouw-tori"
          data-testid="stories-share-cta"
          className="inline-block mt-6 bg-[#B71C1C] text-white font-[700] uppercase tracking-wider py-3 px-8 rounded-full hover:bg-[#8E1515] transition-colors"
        >
          Deel jouw Tori
        </Link>
        <div className="mt-4">
          <ShareButton slug="verhalen" title="Verhalen | De Zeeuwse Tori" label="Deel deze pagina" className="inline-flex items-center gap-2 text-[#1B6B2E] font-[700] text-sm uppercase tracking-wider hover:text-[#0D3B4C] transition-colors" />
        </div>
      </div>

      {stories.length > 0 && <StoriesMap stories={stories} />}

      <div className="mt-10">
        {loading ? (
          <p className="font-[600] text-[#0D3B4C]">Laden...</p>
        ) : stories.length === 0 ? (
          <div className="flex flex-col items-center text-center py-16" data-testid="no-stories">
            <div className="w-16 h-16 rounded-full bg-[#E8F5E9] flex items-center justify-center">
              <MapPin size={30} className="text-[#1B6B2E]" />
            </div>
            <h2 className="text-2xl font-[700] text-[#0D3B4C] mt-5">Nog geen verhalen hier</h2>
            <p className="font-[400] text-[#0D3B4C] mt-2 max-w-md">
              Er zijn nog geen Tori's gedeeld. Wees de eerste en zet jouw plek op de kaart!
            </p>
            <Link
              to="/deel-jouw-tori"
              data-testid="empty-share-cta"
              className="mt-6 bg-[#B71C1C] text-white font-[700] uppercase tracking-wider py-4 px-8 rounded-full hover:bg-[#8E1515] transition-colors"
            >
              Deel jouw Tori
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="stories-grid">
            {stories.map((s) => (
              <StoryCard key={s.id} story={s} onOpen={openLb} />
            ))}
          </div>
        )}
      </div>

      {lb && <Lightbox photos={lb.photos} index={lb.index} onClose={() => setLb(null)} onNav={nav} />}
    </div>
  );
}
